import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { taskAddChange, taskRemovedChange } from './websocket.action';
import { getUsersData, getTasksWaitingWSUpdate } from '../store/application.reducers';
import * as Api from 'utils/api/api';

const MAX_ATTEMPTS = 5;
let CURRENT_STEP = 1;

export const WebSocketConnection = () => {
    const dispatch = useDispatch();
    const userData = useSelector(getUsersData);
    const tasksWaitingForWSResponse = useSelector(getTasksWaitingWSUpdate);

    const wsRef = useRef(null);
    const attemptsRef = useRef(1);

    const closeLiveConnection = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    const parseWsMessage = useCallback((message) => {
        if (message.event === 'TASK_DELETED') {
            dispatch(taskRemovedChange(message.idTask));
        } else if (message.event === 'TASK_CHANGED') {
            if (message.details?.taskAssignmentStatus === "BLOCKED")
                dispatch(taskRemovedChange(message.idTask));
            else
                dispatch(taskAddChange(message.idTask));
        }
    }, [dispatch]);

    const parseWsMessages = useCallback((messages) => {
        if (Array.isArray(messages)) {
            let taskIds = [];
            messages.forEach(message => {
                if (message.event === 'TASK_CHANGED' && message.details?.taskAssignmentStatus !== "BLOCKED") {
                    taskIds.push(message.idTask);
                } else {
                    parseWsMessage(message);
                }
            });
            if (taskIds.length > 0)
                dispatch(taskAddChange(taskIds));
        } else {
            parseWsMessage(messages);
        }
    }, [dispatch, parseWsMessage]);

    const openLiveConnection = useCallback((token) => {
        let wsURL = window.ENVIRONMENT.TASKS_WEBSOCKETS + "?token=" + token;
        let ws = new window.WebSocket(wsURL);

        ws.onopen = () => {
            CURRENT_STEP = 1;
        };

        ws.onmessage = (data) => {
            let messages = JSON.parse(data.data);
            parseWsMessages(messages);
        };

        ws.onclose = (event) => {
            if ((event?.code >= 1010 && event?.code <= 1014) || event?.code === 1006) {
                setTimeout(getWsTokenAndConnect, 5000);
            }
        };

        ws.onerror = () => {
            closeLiveConnection();
        };

        wsRef.current = ws;
    }, [closeLiveConnection, parseWsMessages]);

    const getWsTokenAndConnect = useCallback(() => {
        if (!wsRef.current || (wsRef.current && wsRef.current.readyState === window.WebSocket.CLOSED)) {
            closeLiveConnection();
            Api.getWsToken().then(response => {
                if (response?.webSocketToken)
                    openLiveConnection(response.webSocketToken);
            }).catch(() => {
                attemptsRef.current += 1;
                if (attemptsRef.current <= MAX_ATTEMPTS)
                    setTimeout(getWsTokenAndConnect, 5000);
            });
        }
    }, [closeLiveConnection, openLiveConnection]);

    const checkWSConnection = useCallback(() => {
        if (!wsRef.current || (wsRef.current && wsRef.current.readyState === window.WebSocket.CLOSED))
            getWsTokenAndConnect();
    }, [getWsTokenAndConnect]);

    const handleVisibilityChange = useCallback(() => {
        if (!document.hidden) {
            checkWSConnection();
        }
    }, [checkWSConnection]);

    const onFocus = useCallback(() => {
        if (!wsRef.current || (wsRef.current && wsRef.current.readyState === window.WebSocket.CLOSED)) {
            getWsTokenAndConnect();
        }
    }, [getWsTokenAndConnect]);

    useEffect(() => {
        window.addEventListener("beforeunload", closeLiveConnection, false);
        document.addEventListener('visibilitychange', handleVisibilityChange, true);
        document.addEventListener('focus', onFocus, { capture: true, once: false });

        checkWSConnection();

        return () => {
            window.removeEventListener("beforeunload", closeLiveConnection);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("focus", onFocus);
            closeLiveConnection();
        };
    }, [closeLiveConnection, handleVisibilityChange, onFocus, checkWSConnection]);

    useEffect(() => {
        if (userData === null) {
            closeLiveConnection();
        }
    }, [userData, closeLiveConnection]);

    useEffect(() => {
        checkWSConnection();
    }, [tasksWaitingForWSResponse, checkWSConnection]);

    return null;
};

export default WebSocketConnection;